import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { LogoFull } from '../components/Logo';
import { SEO } from '../components/SEO';
import { useLanguage } from '../lib/LanguageContext';
import { ChevronLeft, ChevronRight, ArrowLeft, CheckCircle2, LayoutGrid, MonitorPlay, LayoutDashboard, Database, Truck, Factory, Warehouse, PackageCheck, Wallet, TrendingUp, CalendarCheck2, MessageCircle } from 'lucide-react';
import { ScrollToTop } from '../components/ScrollToTop';
interface Screen {
  title: string;
  desc: string;
  img: string;
}
// Icons are static — only data/text changes with language
const MODULE_ICONS = [<LayoutDashboard className="w-6 h-6" data-id="element-360" />, <Database className="w-6 h-6" data-id="element-361" />, <Truck className="w-6 h-6" data-id="element-362" />, <Factory className="w-6 h-6" data-id="element-363" />, <Warehouse className="w-6 h-6" data-id="element-364" />, <PackageCheck className="w-6 h-6" data-id="element-365" />, <Wallet className="w-6 h-6" data-id="element-366" />, <CalendarCheck2 className="w-6 h-6" data-id="element-367" />, <TrendingUp className="w-6 h-6" data-id="element-368" />];
function ScreenSlider({
  screens,
  screenshotComing,
  screenshotPreparing,
  screenshotCounter
}: {
  screens: Screen[];
  screenshotComing: string;
  screenshotPreparing: string;
  screenshotCounter: string;
}) {
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [dir, setDir] = useState<'left' | 'right'>('right');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const goTo = (idx: number, d: 'left' | 'right') => {
    if (animating || idx === active || screens.length <= 1) return;
    setDir(d);
    setAnimating(true);
    setTimeout(() => {
      setActive(idx);
      setAnimating(false);
    }, 300);
  };
  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (screens.length <= 1) return;
    timerRef.current = setInterval(() => {
      setDir('right');
      setAnimating(true);
      setTimeout(() => {
        setActive(p => (p + 1) % screens.length);
        setAnimating(false);
      }, 300);
    }, 5000);
  };
  useEffect(() => {
    setActive(0);
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [screens.length]);
  if (screens.length === 0) {
    return <div className="bg-white border-2 border-dashed border-forest-100 rounded-2xl flex flex-col items-center justify-center py-20 px-8 text-center h-full min-h-[300px]" data-id="element-369">
        <div className="w-16 h-16 bg-forest-50 rounded-full flex items-center justify-center mb-4" data-id="element-370">
          <MonitorPlay className="w-8 h-8 text-forest-300" data-id="element-371" />
        </div>
        <p className="text-forest-900 font-semibold" data-id="element-372">{screenshotComing}</p>
        <p className="text-forest-500 text-sm mt-1" data-id="element-373">{screenshotPreparing}</p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-374">
      <div className="relative rounded-xl overflow-hidden bg-white border border-forest-100 shadow-xl shadow-forest-200/30 transition-all duration-300 hover:shadow-2xl" data-id="element-375">
        <div className="h-1 w-full bg-brand-green" data-id="element-376" />
        <div className="h-10 bg-forest-50 border-b border-forest-100 flex items-center px-4 gap-2" data-id="element-377">
          <div className="flex gap-1.5" data-id="element-378">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" data-id="element-379" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" data-id="element-380" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" data-id="element-381" />
          </div>
          <div className="ml-4 flex-1 bg-forest-100 rounded-md h-6 flex items-center px-3 max-w-[200px] border border-forest-200" data-id="element-382">
            <span className="text-[10px] font-medium text-forest-500 opacity-70" data-id="element-383">
              app.sawit-net.com
            </span>
          </div>
          <div className="ml-auto flex items-center gap-1.5 bg-forest-50 px-2 py-0.5 rounded-full border border-forest-100" data-id="element-384">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-green animate-pulse" data-id="element-385" />
            <span className="text-[9px] font-semibold text-forest-600" data-id="element-386">
              Live System
            </span>
          </div>
        </div>

        <div className="relative overflow-hidden bg-forest-50 group" data-id="element-387">
          <div style={{
          opacity: animating ? 0.8 : 1,
          transform: animating ? `translateX(${dir === 'right' ? '-2%' : '2%'}) scale(0.99)` : 'translateX(0) scale(1)',
          transition: 'opacity 300ms ease, transform 300ms ease'
        }} data-id="element-388">
            <img src={screens[active].img} alt={screens[active].title} className="w-full block" data-id="element-389" />
          </div>

          {screens.length > 1 && <>
              <button onClick={() => {
            goTo((active - 1 + screens.length) % screens.length, 'left');
            resetTimer();
          }} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm border border-forest-100 rounded-full shadow-lg flex items-center justify-center text-forest-700 hover:text-forest-900 hover:scale-110 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:-translate-x-[10px] md:group-hover:translate-x-0 duration-300 z-10" data-id="element-390">
                <ChevronLeft className="w-5 h-5" data-id="element-391" />
              </button>
              <button onClick={() => {
            goTo((active + 1) % screens.length, 'right');
            resetTimer();
          }} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm border border-forest-100 rounded-full shadow-lg flex items-center justify-center text-forest-700 hover:text-forest-900 hover:scale-110 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:translate-x-[10px] md:group-hover:translate-x-0 duration-300 z-10" data-id="element-392">
                <ChevronRight className="w-5 h-5" data-id="element-393" />
              </button>
            </>}

          <div className="absolute bottom-4 left-4 right-4 z-10" data-id="element-394">
            <div className="bg-white/95 backdrop-blur-md border border-forest-100 p-3 rounded-lg shadow-lg hidden md:block transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300" data-id="element-395">
              <h5 className="font-bold text-sm text-forest-900" data-id="element-396">
                {screens[active].title}
              </h5>
              <p className="text-xs text-forest-500 line-clamp-1" data-id="element-397">
                {screens[active].desc}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-1" data-id="element-398">
        <div className="flex gap-1.5" data-id="element-399">
          {screens.length > 1 && screens.map((_, i) => <button key={i} onClick={() => {
          goTo(i, i > active ? 'right' : 'left');
          resetTimer();
        }} className={`rounded-full transition-all duration-300 ${active === i ? 'w-8 h-1.5 bg-brand-green' : 'w-1.5 h-1.5 bg-forest-200 hover:bg-forest-300'}`} data-id="element-400" />)}
        </div>
        <span className="text-xs font-medium text-forest-400" data-id="element-401">
          {active + 1} / {screens.length} {screenshotCounter}
        </span>
      </div>

      <div className="block md:hidden mt-2" data-id="element-402">
        <h4 className="font-semibold text-forest-900 text-sm" data-id="element-403">
          {screens[active].title}
        </h4>
        <p className="text-xs text-forest-500 mt-1" data-id="element-404">{screens[active].desc}</p>
      </div>
    </div>;
}
interface FiturPageProps {
  onBack: () => void;
}
export function FiturPage({
  onBack
}: FiturPageProps) {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const f = t.fitur;
  return <div className="min-h-screen bg-white font-sans selection:bg-brand-green/20" data-id="element-405">
      <SEO page="fitur" data-id="element-406" />

      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-forest-100 shadow-sm" data-id="element-407">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 py-4 flex items-center justify-between gap-3" data-id="element-408">
          <div className="flex items-center gap-3" data-id="element-409">
            <button onClick={onBack} className="group flex items-center gap-2 text-forest-500 hover:text-forest-900 text-sm font-medium transition-colors px-3 py-1.5 rounded-lg hover:bg-forest-50" data-id="element-410">
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" data-id="element-411" />
              <span className="hidden sm:inline" data-id="element-412">{f.back}</span>
            </button>
            <div className="w-px h-5 bg-forest-100" data-id="element-413" />
            <button onClick={onBack} className="flex items-center" data-id="element-414">
              <LogoFull size={34} data-id="element-415" />
            </button>
          </div>

          <div className="flex items-center gap-2" data-id="element-416">
            {/* Language Toggle */}
            <div className="flex items-center border border-forest-200 rounded-lg overflow-hidden bg-forest-50 text-xs font-semibold" data-id="element-417">
              <button onClick={() => setLang('id')} className={`px-2.5 py-1.5 transition-colors ${lang === 'id' ? 'bg-brand-green text-white' : 'text-forest-600 hover:text-forest-900 hover:bg-forest-100'}`} data-id="element-418">
                ID
              </button>
              <button onClick={() => setLang('en')} className={`px-2.5 py-1.5 transition-colors ${lang === 'en' ? 'bg-brand-green text-white' : 'text-forest-600 hover:text-forest-900 hover:bg-forest-100'}`} data-id="element-419">
                EN
              </button>
            </div>

            <Button onClick={() => window.open('https://wa.me/628127634091?text=Halo%2C+saya+ingin+menjadwalkan+demo+sawit-net', '_blank')} className="bg-brand-green hover:bg-forest-600 text-white font-semibold h-9 px-4 rounded-lg text-sm shadow-sm hover:shadow-md transition-all" data-id="element-420">
              <span className="hidden sm:inline" data-id="element-421">{f.scheduleDemo}</span>
              <MessageCircle className="w-4 h-4 sm:hidden" data-id="element-422" />
            </Button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative bg-brand-green overflow-hidden py-20 md:py-28" data-id="element-423">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" data-id="element-424" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent z-[1]" data-id="element-425" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-brand-gold/10 rounded-full blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3 z-[1]" data-id="element-426" />

        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center" data-id="element-427">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm mb-8 shadow-lg" data-id="element-428">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" data-id="element-429" />
            <span className="text-white text-xs font-semibold tracking-wide uppercase" data-id="element-430">
              {f.liveBadge}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight leading-tight font-heading" data-id="element-431">
            {f.headline}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 to-brand-gold" data-id="element-432">
              {f.headlineHighlight}
            </span>
          </h1>

          <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-433">
            {f.subtitle}
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-434">
            <div className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white/80 text-sm font-medium flex items-center gap-2" data-id="element-435">
              <LayoutGrid className="w-4 h-4 text-brand-gold" data-id="element-436" />
              {f.modulesCount}
            </div>
            <div className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white/80 text-sm font-medium flex items-center gap-2" data-id="element-437">
              <CheckCircle2 className="w-4 h-4 text-brand-gold" data-id="element-438" />
              {f.realCase}
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto" data-id="element-439">
            {f.groups.map((g, idx) => <a key={g.id} href={`#${g.id}`} className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-medium text-white/80 hover:bg-white/20 hover:text-white transition-all hover:-translate-y-0.5 hover:shadow-lg" data-id="element-440">
                <span className="text-brand-gold group-hover:scale-110 transition-transform" data-id="element-441">
                  {MODULE_ICONS[idx]}
                </span>
                <span data-id="element-442">{g.label}</span>
              </a>)}
          </div>
        </div>
      </div>

      {/* Module Sections */}
      <div className="relative" data-id="element-443">
        {f.groups.map((group, idx) => {
        const isEven = idx % 2 === 0;
        return <section key={group.id} id={group.id} className={`py-20 md:py-28 relative overflow-hidden ${isEven ? 'bg-white' : 'bg-forest-50'}`} data-id="element-444">
              <div className="absolute top-10 left-0 w-full overflow-hidden pointer-events-none select-none" data-id="element-445">
                <span className={`text-[200px] font-bold opacity-[0.04] leading-none absolute -top-20 ${isEven ? '-left-10' : '-right-10'} text-forest-500`} data-id="element-446">
                  0{idx + 1}
                </span>
              </div>

              <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-447">
                <div className={`grid md:grid-cols-2 gap-10 md:gap-12 lg:gap-20 items-center ${isEven ? '' : 'md:grid-flow-dense'}`} data-id="element-448">
                  <div className={`${isEven ? 'md:order-1' : 'md:order-2'}`} data-id="element-449">
                    <div className="flex items-center gap-3 mb-6" data-id="element-450">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm bg-forest-50 text-brand-green" data-id="element-451">
                        {MODULE_ICONS[idx]}
                      </div>
                      <div className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-forest-50 text-forest-600 border border-forest-100" data-id="element-452">
                        {f.moduleLabel} 0{idx + 1}
                      </div>
                    </div>

                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-forest-900 mb-6 leading-tight font-heading" data-id="element-453">
                      {group.label}
                    </h2>

                    <p className="text-forest-700/70 text-lg leading-relaxed mb-8" data-id="element-454">
                      {group.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-8" data-id="element-455">
                      {group.features.map((feat, i) => <div key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-lg border text-xs sm:text-sm font-medium bg-forest-50 border-forest-100 text-forest-700 shadow-sm" data-id="element-456">
                          <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-gold flex-shrink-0" data-id="element-457" />
                          <span className="text-forest-700" data-id="element-458">{feat}</span>
                        </div>)}
                    </div>
                  </div>

                  <div className={`${isEven ? 'md:order-2' : 'md:order-1'}`} data-id="element-459">
                    <ScreenSlider screens={group.screens as Screen[]} screenshotComing={f.screenshotComing} screenshotPreparing={f.screenshotPreparing} screenshotCounter={f.screenshotCounter} data-id="element-460" />
                  </div>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-forest-100 to-transparent opacity-50" data-id="element-461" />
            </section>;
      })}
      </div>

      {/* CTA */}
      <div className="relative bg-brand-green py-20 md:py-32 overflow-hidden" data-id="element-462">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent z-[1]" data-id="element-463" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none z-[1]" data-id="element-464" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-brand-gold/10 rounded-full blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3 z-[1]" data-id="element-465" />

        <div className="container mx-auto px-4 md:px-6 text-center relative z-10" data-id="element-466">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-gold/10 border border-brand-gold/20 mb-6" data-id="element-467">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" data-id="element-468" />
            <span className="text-brand-gold text-xs font-bold tracking-wide uppercase" data-id="element-469">
              {f.ctaBadge}
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 font-heading" data-id="element-470">
            {f.ctaHeadline}
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-471">
            {f.ctaSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-472">
            <Button onClick={() => window.open('https://wa.me/628127634091?text=Halo%2C+saya+ingin+menjadwalkan+demo+sawit-net', '_blank')} className="h-14 px-10 bg-brand-gold hover:bg-gold-400 text-white font-bold rounded-xl text-lg shadow-xl shadow-brand-gold/20 transition-all hover:scale-105 w-full sm:w-auto" data-id="element-473">
              {f.ctaWhatsapp}
            </Button>
            <Button variant="outline" onClick={onBack} className="h-14 px-10 border-white/30 bg-white/10 text-white hover:bg-white/20 hover:border-white/50 rounded-xl font-semibold text-lg w-full sm:w-auto transition-all" data-id="element-474">
              {f.ctaBack}
            </Button>
          </div>

          <p className="text-white/50 text-sm mt-8" data-id="element-475">{f.ctaFooter}</p>
        </div>
      </div>
      <ScrollToTop data-id="element-476" />
    </div>;
}